import React, { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from './AuthContext';

let googleScript;
function loadGoogle() {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (!googleScript) googleScript = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = resolve;
    script.onerror = () => { googleScript = null; script.remove(); reject(new Error('Could not load Google sign-in. Please retry.')); };
    document.head.appendChild(script);
  });
  return googleScript;
}
export default function Login() {
  const { isAuthenticated, acceptSession } = useAuth();
  const button = useRef(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (isAuthenticated) return undefined;
    let cancelled = false;
    const init = async () => {
      setError('');
      try {
        const response = await fetch('/api/auth/config', { credentials: 'same-origin', cache: 'no-store' });
        const config = await response.json();
        if (!response.ok) throw new Error(config.error || 'Sign-in is unavailable.');
        await loadGoogle();
        if (cancelled) return;
        window.google.accounts.id.initialize({
          client_id: config.client_id, nonce: config.nonce,
          auto_select: false, ux_mode: 'popup',
          callback: async ({ credential }) => {
            if (cancelled) return;
            setBusy(true); setError('');
            try {
              const result = await fetch('/api/auth/google', {
                method: 'POST', credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ credential }),
              });
              const data = await result.json();
              if (!result.ok) throw new Error(data.error || 'Sign-in failed.');
              if (!cancelled) acceptSession(data);
            } catch (e) { if (!cancelled) setError(e.message); }
            finally { if (!cancelled) setBusy(false); }
          },
        });
        button.current.replaceChildren();
        window.google.accounts.id.renderButton(button.current, {
          theme: 'outline', size: 'large', width: 300, text: 'signin_with',
        });
      } catch (e) { if (!cancelled) setError(e.message); }
    };
    init();
    return () => { cancelled = true; };
  }, [attempt, isAuthenticated, acceptSession]);
  if (isAuthenticated) return <Navigate to="/" replace />;
  return <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="p-10 bg-white rounded-xl shadow max-w-md w-full space-y-6">
      <h1 className="text-2xl font-bold">Overbrook</h1>
      <p className="text-gray-600">Sign in with your approved work Google account.</p>
      <div ref={button} className={busy ? 'pointer-events-none opacity-50' : ''} />
      {busy && <p>Signing in…</p>}
      {error && <div role="alert" className="text-red-700">
        <p>{error}</p><button className="underline mt-2" onClick={() => setAttempt(n => n + 1)}>Try again</button>
      </div>}
    </div>
  </div>;
}
